import { isSameRoomText, PHOTO_REPORT_CAPTION_MAX_LENGTH } from "@rebuild/shared";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { PhotoRoomLabels } from "../../details-section/Photos/PhotoRoomLabels";
import type { PhotoGalleryItem } from "../../details-section/Photos/photos.types";
import { formatPhotoNumberOverlayTimestamp, getPhotoCapturedAtMs, getPhotoImageUrl } from "../../details-section/Photos/photos.utils";

interface PhotoReportPhotoCardProps {
  item: PhotoGalleryItem;
  /** The group heading, so a path that only repeats it stays off the card — as in the PDF. */
  roomTitle: string;
  /** Live global-sequential number over included photos; undefined when this photo is excluded. */
  photoNumber?: number;
  included: boolean;
  caption: string;
  showPhotoNumberOverlay: boolean;
  includeCaptions: boolean;
  onToggleIncluded: () => void;
  onCaptionChange: (caption: string) => void;
}

export const PhotoReportPhotoCard = ({
  item,
  roomTitle,
  photoNumber,
  included,
  caption,
  showPhotoNumberOverlay,
  includeCaptions,
  onToggleIncluded,
  onCaptionChange,
}: PhotoReportPhotoCardProps) => {
  const timestamp = formatPhotoNumberOverlayTimestamp(getPhotoCapturedAtMs(item));
  const roomPath = item.roomPath && !isSameRoomText(item.roomPath.join(" / "), roomTitle) ? item.roomPath : undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <div className={cn("relative overflow-hidden rounded-md border bg-gray-100", !included && "opacity-50")}>
        <img src={getPhotoImageUrl(item, true)} alt="" loading="lazy" decoding="async" className="aspect-square w-full object-cover" />

        <div className="absolute top-2 left-2">
          <Checkbox checked={included} onCheckedChange={onToggleIncluded} className="border-white bg-white/80 shadow-sm" />
        </div>

        {showPhotoNumberOverlay && included && photoNumber !== undefined && (
          <div className="absolute right-0 bottom-0 left-0 flex items-center justify-between gap-2 bg-black/60 px-2 py-1 text-[11px] font-medium text-white">
            <span>Photo {photoNumber}</span>
            {timestamp && <span className="truncate">{timestamp}</span>}
          </div>
        )}
      </div>

      <PhotoRoomLabels roomPath={roomPath} collapse className="flex" />

      {includeCaptions && (
        <Input
          value={caption}
          onChange={(e) => onCaptionChange(e.target.value)}
          placeholder="Add caption…"
          maxLength={PHOTO_REPORT_CAPTION_MAX_LENGTH}
          disabled={!included}
          className="h-8 text-xs"
        />
      )}
    </div>
  );
};
